import { useMemo, useCallback, useState } from 'react';
import { Button, Stack } from '@mui/material';
import { ErrorOutlineOutlined, TaskAltOutlined, WarningAmberOutlined } from '@mui/icons-material';
import DashboardWidget from '@/components/user-dashboard/common/dashboard-widget';
import type { ContributorSummaryDto } from '@/types/dtos/contributors/response/contributor-summary.dto';
import type { ContributorRoleSummaryDto } from '@/types/dtos/contributors/response/contributor-summary.dto';
import type { EventSummaryDto } from '@/types/dtos/events/response/event-summary.dto';
import type { FlowSummaryDto } from '@/types/dtos/flows/response/flow-summary.dto';
import type { SessionSummaryDto } from '@/types/dtos/sessions/response/session-summary.dto';
import ContributorContributionsList from './contributions.list';
import ConfirmAllModal, { type ConfirmAllModalMode } from './confirm-all-modal';
import { useTranslations } from '@/i18n/use-translations';
import {
  mergeRoleMedia,
  resolveContributorMediaProcessingIssue,
  roleMediaNeedsContributorUploadAction,
  type ContributorMediaProcessingIssue,
} from '@/components/user-dashboard/contributions/contributor-role-media.utils.ts';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import { REMOTE_SPEAKERSHIP_FEATURE } from '@/constants/feature-flags';
import { slotNeedsRemoteSpeakerConfirmation } from '@/utils/remote-speaker-status.utils';

const RolesWidget = ({
  contributor,
  event,
  flows,
  sessions,
}: {
  contributor: ContributorSummaryDto;
  event?: EventSummaryDto;
  flows: FlowSummaryDto[];
  sessions: SessionSummaryDto[];
}) => {
  const t = useTranslations();
  const { hasAccess: hasRemoteSpeakershipAccess, isLoading: isRemoteSpeakershipAccessLoading } =
    useFeatureAccess(REMOTE_SPEAKERSHIP_FEATURE);
  const remoteSpeakershipAvailable = !isRemoteSpeakershipAccessLoading && hasRemoteSpeakershipAccess;

  const flowsById = useMemo(() => new Map(flows.map((flow) => [flow.id, flow])), [flows]);
  const sessionsById = useMemo(() => new Map(sessions.map((session) => [session.id, session])), [sessions]);

  const [selectedRoleIds, setSelectedRoleIds] = useState<Set<string>>(new Set());
  const [selectedSlotIds, setSelectedSlotIds] = useState<Map<string, Set<string>>>(new Map());
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmModalMode, setConfirmModalMode] = useState<ConfirmAllModalMode>('all');

  const getRoleMedia = useCallback(
    (role: ContributorRoleSummaryDto) => {
      if (role.entityType === 'Event') return [];
      if (!role.entityId) return [];
      if (role.entityType === 'Session') {
        return sessionsById.get(role.entityId)?.media ?? [];
      }
      if (role.entityType === 'Flow') {
        return flowsById.get(role.entityId)?.media ?? [];
      }
      return [];
    },
    [flowsById, sessionsById],
  );

  const roleNeedsMediaUploadAction = useCallback(
    (role: ContributorRoleSummaryDto) => {
      const roleMedia = mergeRoleMedia({
        role,
        contributorMedia: contributor.media ?? [],
        entityMedia: getRoleMedia(role),
        eventId: event?.id,
      });
      return roleMediaNeedsContributorUploadAction(roleMedia).needsUploadAction;
    },
    [getRoleMedia, contributor.media, event?.id],
  );

  const allConfirmed = useMemo(() => {
    return contributor.roles.every((role) => {
      if (!role.acceptedAt) return false;
      return (
        role.slots?.every(
          (slot) => !!slot.acceptedAt && !(remoteSpeakershipAvailable && slotNeedsRemoteSpeakerConfirmation(slot)),
        ) ?? true
      );
    });
  }, [contributor.roles, remoteSpeakershipAvailable]);

  const hasAnyMissingMedia = useMemo(() => {
    return contributor.roles.some((role) => role.acceptedAt && roleNeedsMediaUploadAction(role));
  }, [contributor.roles, roleNeedsMediaUploadAction]);

  const worstMediaProcessingIssue = useMemo((): ContributorMediaProcessingIssue | undefined => {
    let hasWarning = false;
    for (const role of contributor.roles) {
      if (!role.acceptedAt) continue;
      const roleMedia = mergeRoleMedia({
        role,
        contributorMedia: contributor.media ?? [],
        entityMedia: getRoleMedia(role),
        eventId: event?.id,
      });
      const issue = resolveContributorMediaProcessingIssue(roleMedia);
      if (issue === 'error') return 'error';
      if (issue === 'warning') hasWarning = true;
    }
    return hasWarning ? 'warning' : undefined;
  }, [contributor.roles, contributor.media, getRoleMedia, event?.id]);

  const mediaHeaderSeverity = useMemo((): 'warning' | 'error' | undefined => {
    if (worstMediaProcessingIssue === 'error') return 'error';
    if (worstMediaProcessingIssue === 'warning') return 'warning';
    return undefined;
  }, [worstMediaProcessingIssue]);

  const hasUnacceptedRolesOrSlots = useMemo(() => {
    return contributor.roles.some(
      (role) =>
        !role.acceptedAt ||
        (role.slots?.some(
          (slot) => !slot.acceptedAt || (remoteSpeakershipAvailable && slotNeedsRemoteSpeakerConfirmation(slot)),
        ) ??
          false),
    );
  }, [contributor.roles, remoteSpeakershipAvailable]);

  const hasNewSelections = useMemo(() => {
    if (selectedRoleIds.size === 0 && selectedSlotIds.size === 0) return false;

    for (const role of contributor.roles) {
      if (selectedRoleIds.has(role.id) && !role.acceptedAt) {
        return true;
      }
      const roleSlotIds = selectedSlotIds.get(role.id);
      if (!roleSlotIds || roleSlotIds.size === 0) continue;
      for (const slotId of roleSlotIds) {
        const slot = role.slots?.find((s) => s.id === slotId);
        if (!slot) continue;
        if (!slot.acceptedAt) return true;
        if (remoteSpeakershipAvailable && slotNeedsRemoteSpeakerConfirmation(slot)) return true;
      }
    }
    return false;
  }, [contributor.roles, selectedRoleIds, selectedSlotIds, remoteSpeakershipAvailable]);

  const handleRoleSelectionChange = useCallback((roleId: string, selected: boolean) => {
    setSelectedRoleIds((prev) => {
      const next = new Set(prev);
      if (selected) {
        next.add(roleId);
      } else {
        next.delete(roleId);
      }
      return next;
    });

    // Deselecting a role also clears its selected slots
    if (!selected) {
      setSelectedSlotIds((prev) => {
        if (!prev.has(roleId)) return prev;
        const next = new Map(prev);
        next.delete(roleId);
        return next;
      });
    }
  }, []);

  const handleSlotSelectionChange = useCallback(
    (roleId: string, slotId: string, selected: boolean) => {
      const role = contributor.roles.find((entry) => entry.id === roleId);
      const slot = role?.slots?.find((entry) => entry.id === slotId);
      const selectingRoleOrSlotAcceptance = Boolean(role && slot && (!role.acceptedAt || !slot.acceptedAt));

      setSelectedSlotIds((prev) => {
        const next = new Map(prev);
        const previousSlotSet = next.get(roleId);
        const slotSet = new Set(previousSlotSet ?? []);
        if (selected) {
          slotSet.add(slotId);
          next.set(roleId, slotSet);
        } else {
          slotSet.delete(slotId);
          if (slotSet.size === 0) {
            next.delete(roleId);
          } else {
            next.set(roleId, slotSet);
          }
        }
        return next;
      });

      // Auto-check the role only when confirming the role/slot itself — not for remote-only follow-ups.
      if (selected && selectingRoleOrSlotAcceptance) {
        setSelectedRoleIds((prev) => {
          const next = new Set(prev);
          next.add(roleId);
          return next;
        });
      }
    },
    [contributor.roles],
  );

  const clearSelections = useCallback(() => {
    setSelectedRoleIds(new Set());
    setSelectedSlotIds(new Map());
  }, []);

  const openConfirmModal = useCallback((mode: ConfirmAllModalMode) => {
    setConfirmModalMode(mode);
    setConfirmModalOpen(true);
  }, []);

  const getTitle = () => {
    if (allConfirmed) {
      return t('user.contributionsList.allContributionsConfirmed');
    }
    return t('user.contributionsList.confirmContributions');
  };

  const getMediaSubtitle = () => {
    if (worstMediaProcessingIssue === 'error') {
      return t('user.contributionsList.mediaProcessingError');
    }
    if (worstMediaProcessingIssue === 'warning') {
      return t('user.contributionsList.mediaProcessingWarning');
    }
    if (hasAnyMissingMedia) {
      return t('user.contributionsList.provideMediaForParticipations');
    }
    return undefined;
  };

  const headerAvatar = (() => {
    if (hasUnacceptedRolesOrSlots) {
      return <TaskAltOutlined color="primary" />;
    }
    if (mediaHeaderSeverity === 'error') {
      return <ErrorOutlineOutlined color="error" />;
    }
    if (mediaHeaderSeverity === 'warning') {
      return <WarningAmberOutlined color="warning" />;
    }
    if (hasAnyMissingMedia) {
      return <TaskAltOutlined color="primary" />;
    }
    return <TaskAltOutlined color="primary" />;
  })();

  return (
    <>
      <DashboardWidget
        title={getTitle()}
        titleColor={
          hasUnacceptedRolesOrSlots || hasAnyMissingMedia || !mediaHeaderSeverity ? 'primary.main' : undefined
        }
        subtitle={getMediaSubtitle()}
        subtitleColor={
          mediaHeaderSeverity === 'error'
            ? 'error.main'
            : mediaHeaderSeverity === 'warning'
              ? 'warning.main'
              : hasAnyMissingMedia
                ? 'primary.main'
                : undefined
        }
        avatar={headerAvatar}
        footer={
          hasUnacceptedRolesOrSlots ? (
            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ px: 1, py: 0.5 }}>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                disabled={!hasNewSelections}
                onClick={() => openConfirmModal('selected')}
              >
                {t('user.contributionsList.confirmSelected')}
              </Button>
              <Button variant="contained" color="primary" size="small" onClick={() => openConfirmModal('all')}>
                {t('user.contributionsList.confirmAll')}
              </Button>
            </Stack>
          ) : undefined
        }
      >
        <ContributorContributionsList
          contributor={contributor}
          event={event}
          flows={flows}
          sessions={sessions}
          mode="widget"
          onRoleSelectionChange={handleRoleSelectionChange}
          onSlotSelectionChange={handleSlotSelectionChange}
          selectedRoleIds={selectedRoleIds}
          selectedSlotIds={selectedSlotIds}
        />
      </DashboardWidget>
      <ConfirmAllModal
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        contributor={contributor}
        event={event}
        flows={flows}
        sessions={sessions}
        mode={confirmModalMode}
        selectedRoleIds={selectedRoleIds}
        selectedSlotIds={selectedSlotIds}
        remoteSpeakershipEnabled={remoteSpeakershipAvailable}
        onSuccess={clearSelections}
      />
    </>
  );
};

export default RolesWidget;
